// 1. Add InputHTMLAttributes to the imports
import { forwardRef, InputHTMLAttributes } from "react";
import { BaseInputProps } from "../../types";
import styles from "./TextInput.module.scss";

// 2. Update the interface
// We extend standard HTML input props, but Omit 'type' (since we override it)
// and 'size' (to avoid conflicts if you use size for styling)
export interface TextInputProps
  extends Omit<
      InputHTMLAttributes<HTMLInputElement>,
      "type" | "size" | "prefix" | "suffix" | "name"
    >,
    // FIX: Omit prefix and suffix from BaseInputProps too
    Omit<BaseInputProps, "prefix" | "suffix"> {
  /** Input type */
  type?: "text" | "email" | "tel" | "url" | "number";

  /** Prefix icon or text */
  prefix?: React.ReactNode;

  /** Suffix icon or text */
  suffix?: React.ReactNode;
}

/**
 * TextInput Component
 * ... (rest of your comments)
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      error,
      helperText,
      name,
      required = false,
      fullWidth = true,
      type = "text",
      prefix,
      suffix,
      disabled,
      className,
      ...props // 3. Now 'props' contains valid HTML attributes (onChange, value, etc.)
    },
    ref
  ) => {
    const inputId = `input-${name}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const hasError = Boolean(error);

    return (
      <div
        className={`${styles.container} ${fullWidth ? styles.fullWidth : ""} ${className || ""}`}
      >
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className={styles.inputWrapper}>
          {prefix && <span className={styles.prefix}>{prefix}</span>}

          <input
            ref={ref}
            id={inputId}
            name={name}
            type={type}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              [errorId, helperId].filter(Boolean).join(" ") || undefined
            }
            aria-required={required}
            className={`${styles.input} ${hasError ? styles.inputError : ""} ${prefix ? styles.hasPrefix : ""} ${suffix ? styles.hasSuffix : ""}`}
            // 4. This spread now works because we extended InputHTMLAttributes
            {...props}
          />

          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className={styles.helper}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";
